import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { CustomReportsApi } from 'api/custom-reports';
import { WidgetAPI } from 'api/dashboard';
import type {
  CreateWidgetPayload,
  EditWidgetPayload,
  PreviewWidgetPayload,
  SimpleFilter,
  ChartOptions,
  DrillDownPayload,
  WidgetResponse,
  SimpleSearchFilter,
  WidgetOrder,
  DrillDownExportResponse,
  ColumnMeta,
  WidgetSourceType,
  Widget as DashboardWidget,
} from 'api/dashboard/widget';
import { Project } from 'api/project';
import type {
  ChartOptions as ChartOptionCustom,
  WidgetPayload,
  ReportingSourceType,
  FieldCustomType,
  PivotFields,
  Widget,
} from 'components/pages/custom-reports/types';
import {
  PreviewWidgetPayload as PreviewWidgetPayloadCustom,
  WidgetResponse as WidgetResponseCustom,
} from 'components/pages/custom-reports/types';
import { getDashboardQueryKey, getDashboardQueryKeyV2 } from './dashboard';

type ResolveWidgetQueryKey = [string, number];
const getResolveWidgetQueryKey = (widgetId: number): ResolveWidgetQueryKey => [
  'resolve-widget',
  widgetId,
];

type QueryWidgetKey = [string, number];
export const getQueryWidgetKey = (widgetId: number): QueryWidgetKey => [
  'widget',
  widgetId,
];

export type QueryWidgetsBySourceKey = [string, WidgetSourceType];
export const getQueryWidgetsBySourceKey = (
  sourceType: WidgetSourceType,
): QueryWidgetsBySourceKey => ['widgets', sourceType];

export const getQueryWidgetKeyForDrillDown = (
  widgetId: number,
): QueryWidgetKey => ['widget-drilldown', widgetId];

export const getResolveMultipleWidgetsQueryKey = (
  widgetIds: number[],
  simpleSearchFilter: SimpleSearchFilter | null = null,
): (string | number | SimpleSearchFilter | null)[] => [
  'resolve-multiple',
  ...widgetIds,
  simpleSearchFilter,
];

export const getResolveMultipleWidgetsQueryKeyV1 = (
  payload: WidgetPayload,
): (string | WidgetPayload)[] => ['resolve-multiple-v1', payload];

export const getResolveMultipleWidgetsQueryKeyV2 = (
  payload: WidgetPayload,
): (string | WidgetPayload)[] => ['resolve-multiple-v2', payload];

const useResolveWidgetQuery = (
  widgetId: number,
  options: UseQueryOptions<
    ChartOptions,
    unknown,
    ChartOptions,
    ResolveWidgetQueryKey
  > = {},
): UseQueryResult<ChartOptions> => {
  return useQuery(
    getResolveWidgetQueryKey(widgetId),
    () => WidgetAPI.resolveWidget(widgetId),
    { ...options, retry: 1, refetchOnWindowFocus: false },
  );
};

const useResolveMultipleWidgetsQuery = (
  dashboardId: number,
  widgetIds: number[] = [],
  simpleSearchFilter: SimpleSearchFilter | null = null,
  options: UseQueryOptions<
    WidgetResponse[],
    unknown,
    WidgetResponse[],
    (string | number | SimpleSearchFilter | null)[]
  > = {},
): UseQueryResult<WidgetResponse[]> => {
  return useQuery(
    getResolveMultipleWidgetsQueryKey(widgetIds, simpleSearchFilter),
    () =>
      WidgetAPI.resolveMultipleWidgets(
        dashboardId,
        widgetIds,
        simpleSearchFilter,
      ),
    { ...options, retry: 1, refetchOnWindowFocus: false },
  );
};

const useResolveMultipleWidgetsQueryV1 = (
  payload: WidgetPayload,
  options: UseQueryOptions<
    WidgetResponseCustom[],
    unknown,
    WidgetResponseCustom[],
    (string | WidgetPayload)[]
  > = {},
): UseQueryResult<WidgetResponseCustom[]> => {
  return useQuery(
    getResolveMultipleWidgetsQueryKeyV1(payload),
    () => WidgetAPI.resolveMultipleWidgetsV1(payload),
    { ...options, retry: 1, refetchOnWindowFocus: false },
  );
};

const useResolveMultipleWidgetsQueryV2 = (
  payload: WidgetPayload,
  options: UseQueryOptions<
    WidgetResponseCustom[],
    unknown,
    WidgetResponseCustom[],
    (string | WidgetPayload)[]
  > = {},
): UseQueryResult<WidgetResponseCustom[]> => {
  return useQuery(
    getResolveMultipleWidgetsQueryKeyV2(payload),
    () => WidgetAPI.resolveMultipleWidgetsV2(payload),
    { ...options, retry: 1, refetchOnWindowFocus: false },
  );
};

const useWidgetQuery = <WidgetType = Widget>(
  widgetId: number,
  options: UseQueryOptions<
    WidgetType,
    unknown,
    WidgetType,
    QueryWidgetKey
  > = {},
): UseQueryResult<WidgetType> => {
  return useQuery(
    getQueryWidgetKey(widgetId),
    () => WidgetAPI.getWidget<WidgetType>(widgetId),
    {
      ...options,
    },
  );
};

const useWidgetQueryDashboard = (
  widgetId: number,
  options: UseQueryOptions<
    DashboardWidget,
    unknown,
    DashboardWidget,
    QueryWidgetKey
  > = {},
): UseQueryResult<DashboardWidget> => {
  return useQuery(
    getQueryWidgetKey(widgetId),
    () =>
      WidgetAPI.getWidget(widgetId).then(
        (widget) => widget as unknown as DashboardWidget,
      ),
    {
      ...options,
    },
  );
};

export const previewWidgetQueryKey = (): [string] => ['preview-widget'];
export const previewWidgetQueryKeyV1 = (): [string] => ['preview-widget-v1'];

const usePreviewWidgetQuery = (
  payload: PreviewWidgetPayload,
): UseQueryResult<ChartOptions> => {
  return useQuery([previewWidgetQueryKey(), payload], () =>
    WidgetAPI.previewWidget(payload),
  );
};

const usePreviewWidgetQueryV1 = (
  payload: PreviewWidgetPayloadCustom,
): UseQueryResult<ChartOptionCustom> => {
  return useQuery([previewWidgetQueryKeyV1(), payload], () =>
    WidgetAPI.previewWidgetV1(payload),
  );
};

const useDrillWidgetMutation = (widgetId: number) => {
  return useMutation<Project[], unknown, SimpleFilter>((payload) =>
    WidgetAPI.drillWidget(widgetId, payload),
  );
};

const useDrillWidgetMutationV2 = (widgetId: number) => {
  return useMutation<Project[], unknown, DrillDownPayload>((payload) =>
    WidgetAPI.drillWidgetV2(widgetId, payload),
  );
};

const useUpdateWidgetOrderMutation = (
  dashbaordId: number,
  options: UseMutationOptions<void, unknown, WidgetOrder[]> = {},
): UseMutationResult<void, unknown, WidgetOrder[]> =>
  useMutation((payload) => WidgetAPI.updateWidgetOrder(dashbaordId, payload), {
    ...options,
  });
const useUpdateWidgetOrderBySourceMutation = (
  sourceType: WidgetSourceType,
  options: UseMutationOptions<void, unknown, WidgetOrder[]> = {},
): UseMutationResult<void, unknown, WidgetOrder[]> =>
  useMutation(
    (payload) => WidgetAPI.updateWidgetOrderBySourceId(sourceType, payload),
    {
      ...options,
    },
  );

const useCreateWidgetMutation = (): UseMutationResult<
  DashboardWidget,
  unknown,
  CreateWidgetPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<DashboardWidget, unknown, CreateWidgetPayload>(
    (payload) =>
      WidgetAPI.create(payload).then(
        (widget) => widget as unknown as DashboardWidget,
      ),
    {
      onSuccess: async (newWidget, { dashboardId }) => {
        const key = getDashboardQueryKey(Number(dashboardId));

        await queryClient.invalidateQueries(key);
      },
    },
  );
};

const useCreateWidgetMutationV2 = (): UseMutationResult<
  Widget,
  unknown,
  CreateWidgetPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<Widget, unknown, CreateWidgetPayload>(
    (payload) =>
      WidgetAPI.create(payload, true).then(
        (widget) => widget as unknown as Widget,
      ),
    {
      onSuccess: async (newWidget, { dashboardId }) => {
        const key = getDashboardQueryKeyV2(Number(dashboardId));

        await queryClient.invalidateQueries(key);
      },
    },
  );
};

export type EditWidgetMutationPayload = EditWidgetPayload &
  Pick<Widget, 'widgetId'> & { dashboardId?: number };

const useEditWidgetMutation = (
  isEditingColumnOrder = false,
): UseMutationResult<Widget, unknown, EditWidgetMutationPayload> => {
  const queryClient = useQueryClient();

  return useMutation<Widget, unknown, EditWidgetMutationPayload>(
    ({ widgetId, ...payload }) =>
      WidgetAPI.editWidget(widgetId, payload).then(
        (widget) => widget as unknown as Widget,
      ),
    {
      onSuccess: async (res, variables) => {
        if (isEditingColumnOrder) {
          await queryClient.refetchQueries(
            getQueryWidgetKeyForDrillDown(res.widgetId),
          );
        } else {
          // Use dashboardId from variables - refetch immediately to prevent race conditions
          await queryClient.refetchQueries(
            getDashboardQueryKeyV2(variables.dashboardId || 0),
          );
        }
      },
    },
  );
};

export type EditWidgetMutationPayloadDashboard = EditWidgetPayload &
  Pick<DashboardWidget, 'widgetId'> & { dashboardId?: number };

const useEditWidgetMutationDashboard = (
  isEditingColumnOrder = false,
): UseMutationResult<
  DashboardWidget,
  unknown,
  EditWidgetMutationPayloadDashboard
> => {
  const queryClient = useQueryClient();

  return useMutation<
    DashboardWidget,
    unknown,
    EditWidgetMutationPayloadDashboard
  >(
    ({ widgetId, ...payload }) =>
      WidgetAPI.editWidget(widgetId, payload).then(
        (widget) => widget as unknown as DashboardWidget,
      ),
    {
      onSuccess: async (res, variables) => {
        if (isEditingColumnOrder) {
          await queryClient.refetchQueries(
            getQueryWidgetKeyForDrillDown(res.widgetId),
          );
        } else {
          // Use dashboardId from variables - refetch immediately to prevent race conditions
          await queryClient.refetchQueries(
            getDashboardQueryKey(variables.dashboardId || 0),
          );
          await queryClient.refetchQueries(
            getDashboardQueryKeyV2(variables.dashboardId || 0),
          );
        }
      },
    },
  );
};

const useDeleteWidgetMutation = (): UseMutationResult<
  void,
  unknown,
  { widgetId: number; dashboardId: number }
> => {
  const queryClient = useQueryClient();

  return useMutation(({ widgetId }) => WidgetAPI.deleteWidget(widgetId), {
    onSuccess: async (_, { dashboardId }) => {
      const key = getDashboardQueryKeyV2(Number(dashboardId));
      const keyV1 = getDashboardQueryKey(Number(dashboardId));

      await queryClient.invalidateQueries(key);
      await queryClient.invalidateQueries(keyV1);
    },
  });
};

const useWidgetExportsMutation = (
  widgetId: number,
  options: UseMutationOptions<
    DrillDownExportResponse,
    unknown,
    DrillDownPayload
  > = {},
): UseMutationResult<DrillDownExportResponse, unknown, DrillDownPayload> => {
  return useMutation(
    (payload) => WidgetAPI.drillDownExport(widgetId, payload),
    {
      ...options,
    },
  );
};

const useDrillDownColumnOrderQuery = (
  widgetId: number,
  options: UseQueryOptions<
    ColumnMeta,
    unknown,
    ColumnMeta,
    ReturnType<typeof getQueryWidgetKeyForDrillDown>
  > = {},
): UseQueryResult<ColumnMeta> => {
  return useQuery(
    getQueryWidgetKeyForDrillDown(widgetId),
    () => WidgetAPI.getDrillDownColumnOrder(widgetId),
    {
      ...options,
    },
  );
};

// fetch widgets which are not part of dashboards
const useGetAllWidgetsBySource = <WidgetType = Widget>(
  sourceType: WidgetSourceType,
  options: UseQueryOptions<
    WidgetType[],
    unknown,
    WidgetType[],
    QueryWidgetsBySourceKey
  > = {},
): UseQueryResult<WidgetType[], unknown> =>
  useQuery(
    getQueryWidgetsBySourceKey(sourceType),
    () => WidgetAPI.getWidgetsBySource<WidgetType>(sourceType),
    options,
  );

// Widget fields query
type WidgetFieldsQueryKey = [string, ReportingSourceType];
const getWidgetFieldsQueryKey = (
  sourceType: ReportingSourceType,
): WidgetFieldsQueryKey => ['widget-fields', sourceType];

// Widget pivot fields query
type WidgetPivotFieldsQueryKey = [string, ReportingSourceType];
const getWidgetPivotFieldsQueryKey = (
  sourceType: ReportingSourceType,
): WidgetPivotFieldsQueryKey => ['widget-pivot-fields', sourceType];

const useWidgetFieldsQuery = (
  sourceType: ReportingSourceType | undefined,
  options: UseQueryOptions<
    FieldCustomType,
    unknown,
    FieldCustomType,
    WidgetFieldsQueryKey
  > = {},
): UseQueryResult<FieldCustomType> => {
  return useQuery(
    getWidgetFieldsQueryKey(sourceType!),
    async () => {
      const fieldsData = await CustomReportsApi.getFields(sourceType!);

      // Apply the same transformation as custom reports
      const result = Object.keys(fieldsData).reduce((acc, curr) => {
        const groupFields = fieldsData[curr];

        // Preserve server-provided operation as-is (allow AVG)
        acc[curr] = groupFields;

        return acc;
      }, {} as FieldCustomType);

      return result;
    },
    {
      ...options,
      enabled: !!sourceType && (options.enabled ?? true),
      retry: 1,
      refetchOnWindowFocus: false,
    },
  );
};

const useWidgetPivotFieldsQuery = (
  sourceType: ReportingSourceType | undefined,
  options: UseQueryOptions<
    PivotFields,
    unknown,
    PivotFields,
    WidgetPivotFieldsQueryKey
  > = {},
): UseQueryResult<PivotFields> => {
  return useQuery(
    getWidgetPivotFieldsQueryKey(sourceType!),
    () => CustomReportsApi.getPivotFields(sourceType!),
    {
      ...options,
      enabled: !!sourceType && (options.enabled ?? true),
      retry: 1,
      refetchOnWindowFocus: false,
    },
  );
};

export {
  useCreateWidgetMutation,
  useCreateWidgetMutationV2,
  useDeleteWidgetMutation,
  useResolveWidgetQuery,
  useResolveMultipleWidgetsQuery,
  useResolveMultipleWidgetsQueryV1,
  useResolveMultipleWidgetsQueryV2,
  useWidgetQuery,
  useWidgetQueryDashboard,
  useEditWidgetMutation,
  useEditWidgetMutationDashboard,
  useDrillWidgetMutation,
  getResolveWidgetQueryKey,
  usePreviewWidgetQuery,
  usePreviewWidgetQueryV1,
  useDrillWidgetMutationV2,
  useUpdateWidgetOrderMutation,
  useUpdateWidgetOrderBySourceMutation,
  useWidgetExportsMutation,
  useDrillDownColumnOrderQuery,
  useGetAllWidgetsBySource,
  useWidgetFieldsQuery,
  useWidgetPivotFieldsQuery,
};
